<template>
  <mf-loading-dialog :loading="loading || loadingAnalyticConfiguration">
    <base-page-layout title="Faturamento incremental" class="px-7">
      <template v-if="!isBackoffice" v-slot:header>
        <div class="header-actions">
          <dre-pdf
            :subtitle="getPdfSubtitle"
            :loading="loading"
            :analysed-period="analysedPeriod"
            :period-id="analysedPeriodId"
            :period="period"
            :analysis-initial-date="dreInitialDate"
            :formatted-date-label="formattedDateLabel"
            :analysed-period-weekly="analysedPeriodWeekly"
          />

          <start-tour-button :flow-id="flowId" />
        </div>
      </template>

      <div v-if="!isBackoffice">
        <div class="filters-container mb-2">
          <analysis-configuration is-dre @change-analysis-configuration="handleSelectAnalyticConfiguration" />
        </div>
        <div class="period-container">
          <h2 class="mb-2">Período Analisado</h2>
          <p class="mb-4">Selecione o mês que deseja visualizar a análise do Faturamento incremental</p>
        </div>
        <div class="d-flex align-center justify-space-between mb-6 padding-10">
          <week-range-picker
            v-if="showWeekRangePicker"
            :analysed-period.sync="analysedPeriodWeekly"
            :min="minAnalyticPeriod"
            :max="maxAnalyticPeriod"
            :initial-weekday="initialWeekday"
            @input="changeDate"
            @set-date="formattedDateLabel = $event"
          />
          <month-range-picker
            v-else
            :analysed-period.sync="analysedPeriod"
            :min="minAnalyticPeriod"
            :max="maxAnalyticPeriod"
            :is-not-recurrent="isIsolated"
            :not-recurrent-analyzed-period="notRecurrentAnalyzedPeriod"
            @input="changeDate"
            @set-date="formattedDateLabel = $event"
          />
        </div>
        <mf-info-card
          v-if="!hasDreOffersSchedulesResults"
          class="mb-5"
          description="Não existe resultado em nenhuma campanha de oferta no período selecionado. Pode ser que nenhuma oferta foi criada ou ainda não obteve registro de venda nesse período, por isso o faturamento incremental está zerado."
          icon="warning"
          variant="warning"
          title="Atenção"
          cta-label="Ir para Gestão de ofertas"
          cta-icon="arrow_forward"
          @cta-click="$router.push('/campanhas/gestao-ofertas')"
        />
        <incremental-revenue-card
          :formatted-date-label="formattedDateLabel"
          :dre-period-id="analysedPeriodId"
          :analysed-period="analysedPeriod"
          :analysed-period-weekly="analysedPeriodWeekly"
        />
        <div data-userflow-id="club-offers">
          <h2 class="club-offers mt-8 mb-5">Ofertas do clube</h2>
          <div class="club-card-container">
            <club-card variant="positive" />
            <club-card variant="negative" />
          </div>
        </div>
        <products-offers-carrousel />
        <h2 class="consumers-distribution mt-8 mb-5">Consumidores</h2>
        <div class="distribution-container">
          <consumers-distribution-card
            :analysed-period-id="analysedPeriodId"
            :analysed-period="consumersPeriod"
            :analysis-initial-date="dreInitialDate"
            :is-weekly-analysis="showWeekRangePicker"
          />
          <channels-distribution-card :period-id="analysedPeriodId" :period="period" />
        </div>
      </div>

      <backoffice-not-allowed v-else />
    </base-page-layout>
  </mf-loading-dialog>
</template>

<script>
import BasePageLayout from '@/components/BasePageLayout.vue'
import MonthRangePicker from '../components/MonthRangePicker.vue'
import WeekRangePicker from '../components/WeekRangePicker.vue'
import ClubCard from '../components/club-offers/ClubCard.vue'
import IncrementalRevenueCard from '../components/incremental-revenue/IncrementalRevenueCard.vue'
import ProductsOffersCarrousel from '../components/products-offers/ProductsOffersCarrousel.vue'
import ConsumersDistributionCard from '../components/consumers-distribution/ConsumersDistributionCard.vue'
import ChannelsDistributionCard from '../components/consumers-distribution/ChannelsDistributionCard.vue'
import DrePdf from '../components/pdf/DrePdf.vue'
import BackofficeNotAllowed from '../components/BackofficeNotAllowed.vue'
import AnalysisConfiguration from '@/modules/clientes/rfv/views/components/AnalysisConfiguration.vue'
import { FATURAMENTO_INCREMENTAL_PAGE_FLOW_ID } from '@/config/userflow.js'

import { DateTime } from 'luxon'
import { mapActions, mapGetters, mapMutations } from 'vuex'

import userflow from 'userflow.js'

export default {
  components: {
    BasePageLayout,
    MonthRangePicker,
    WeekRangePicker,
    ClubCard,
    IncrementalRevenueCard,
    ProductsOffersCarrousel,
    ConsumersDistributionCard,
    ChannelsDistributionCard,
    DrePdf,
    BackofficeNotAllowed,
    AnalysisConfiguration,
    StartTourButton: () => import('@/components/tour/StartTourButton.vue'),
  },
  data: () => ({
    analysedPeriod: '',
    analysedPeriodId: '',
    analysedPeriodWeekly: ['', ''],
    minAnalyticPeriod: '',
    maxAnalyticPeriod: '',
    period: ['', ''],
    consumersPeriod: ['', ''],
    formattedDateLabel: '',
    loading: false,
    loadingAnalyticConfiguration: false,
  }),
  computed: {
    ...mapGetters(['analyticsConfiguration', 'analyticAllPeriods', 'dreAnalysedPeriod', 'hasDreOffersSchedulesResults', 'dreAnalyticConfigurations']),
    showWeekRangePicker() {
      return this.analyticsConfiguration?.recurrent?.modelType === 'WEEKLY'
    },
    isIsolated() {
      return this.analyticsConfiguration?.model === 'NOT_RECURRENT'
    },
    notRecurrentAnalyzedPeriod() {
      return this.analyticsConfiguration?.notRecurrent?.analyzedPeriod
    },
    initialWeekday() {
      return this.analyticsConfiguration?.recurrent?.initialWeekDay
    },
    dreInitialDate() {
      if (this.showWeekRangePicker) return DateTime.fromFormat(this.analysedPeriodWeekly[0], 'yyyy-MM-dd').toFormat('yyyyMMdd')
      if (!this.analysedPeriod) return ''
      return DateTime.fromFormat(this.analysedPeriod, 'yyyy-MM').startOf('month').toFormat('yyyyMMdd')
    },
    getPdfSubtitle() {
      if (this.showWeekRangePicker) return this.formattedDateLabel
      const period = DateTime.fromFormat(this.dreAnalysedPeriod, 'yyyy-MM').toFormat('MMMM yyyy')
      return period[0].toUpperCase() + period.substring(1)
    },
    flowId() {
      return FATURAMENTO_INCREMENTAL_PAGE_FLOW_ID
    },
    isBackoffice() {
      return this.$getLoggedUser()?.integrationType === 'backoffice'
    },
  },
  async created() {
    if (!this.isBackoffice) {
      const { analysisId } = this.$route?.params

      try {
        this.loading = true
        await this.$store.dispatch('getStates', ['getLastUpdate'])
        await this.getAnalyticsConfigurations({ variables: { type: 'DRE' } })
        const defaultAnalysis = analysisId
          ? this.dreAnalyticConfigurations.find((analysis) => analysis._id === analysisId)
          : this.dreAnalyticConfigurations.find((analysis) => analysis.default)
        await this.handleSelectAnalyticConfiguration(defaultAnalysis)
        userflow.identify(this.$getLoggedUser().id_user, {
          name: this.$getLoggedUser().name,
          email: this.$getLoggedUser().username,
          client: this.$getLoggedUser().client,
          signed_up_at: new Date().toISOString(),
          profiles: this.$getLoggedUser().profiles?.toString(),
          isAdmin: this.$getLoggedUser().mf,
          isFranchise: this.$getLoggedUser().is_franchise,
          isFranchisee: this.$getLoggedUser().is_franchisee,
        })
        userflow.track('page_is_loaded')
      } catch (error) {
        this.$snackbar({ message: 'Erro ao buscar dados do Faturamento incremental.', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    }
  },
  methods: {
    ...mapMutations(['SET_ANALYTICS_CONFIGURATION']),
    ...mapActions([
      'getDreRevenueIncrementMetrics',
      'getDreOffersCouponsMetrics',
      'getDreProductsMetrics',
      'getDreClientsDistributionBefore',
      'getDreChannelsDistribution',
      'getAnalyticsConfiguration',
      'getAllAnalyticPeriods',
      'setDreAnalysedPeriod',
      'getDreOffersSchedulesResult',
      'getAnalyticsConfigurations',
    ]),
    async handleSelectAnalyticConfiguration(analysis) {
      try {
        this.loadingAnalyticConfiguration = true
        await this.getPeriods(analysis)
        await this.getData()
      } catch (error) {
        console.log(error)
      } finally {
        this.loadingAnalyticConfiguration = false
      }
    },
    async getPeriods(analysis) {
      this.SET_ANALYTICS_CONFIGURATION(analysis)
      await this.getAllAnalyticPeriods({ analyticConfigurationsIds: this.analyticsConfiguration?._id })
      if (this.analyticAllPeriods.length > 0) {
        this.analysedPeriodId = this.analyticAllPeriods[0]._id
        if (this.showWeekRangePicker) {
          this.analysedPeriodWeekly = [this.analyticAllPeriods[0].initialDate, this.analyticAllPeriods[0].finalDate]
          this.minAnalyticPeriod = this.analyticAllPeriods[this.analyticAllPeriods.length - 1].initialDate
          this.maxAnalyticPeriod = this.analysedPeriodWeekly[1]
          this.formattedDateLabel = `${DateTime.fromFormat(this.analysedPeriodWeekly[0], 'yyyy-MM-dd').toFormat('dd/MM/yyyy')} - ${DateTime.fromFormat(
            this.analysedPeriodWeekly[1],
            'yyyy-MM-dd'
          ).toFormat('dd/MM/yyyy')}`
        } else {
          this.analysedPeriod = DateTime.fromFormat(this.analyticAllPeriods[0].initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
          this.setDreAnalysedPeriod(this.analysedPeriod)
          this.minAnalyticPeriod = DateTime.fromFormat(this.analyticAllPeriods[this.analyticAllPeriods.length - 1].initialDate, 'yyyy-MM-dd').toFormat(
            'yyyy-MM'
          )
          this.maxAnalyticPeriod = this.analysedPeriod
        }
        this.period = [
          DateTime.fromFormat(this.analyticAllPeriods[0].initialDate, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
          DateTime.fromFormat(this.analyticAllPeriods[0].finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        ]
      }
    },
    async getData() {
      try {
        const input = {
          input: {
            analyticConfigurationId: this.analyticsConfiguration?._id,
            periodId: this.analysedPeriodId,
          },
        }
        await Promise.all([
          this.getDreOffersSchedulesResult(input),
          this.getDreRevenueIncrementMetrics(input),
          this.getDreOffersCouponsMetrics(input),
          this.getDreProductsMetrics(input),
          this.getDreClientsDistributionBefore(input),
          this.getDreChannelsDistribution(input),
        ])
      } catch (error) {
        console.log(error)
        this.$snackbar({ message: 'Não foi possível encontrar dados para o período selecionado.', snackbarColor: 'error' })
      } finally {
        this.loading = false
      }
    },
    async changeDate(value) {
      if (value === this.analysedPeriod) return
      let newCurrentPeriod = null

      if (this.showWeekRangePicker) {
        newCurrentPeriod = this.analyticAllPeriods.find((period) => period?.initialDate === value[0])
        this.analysedPeriodId = newCurrentPeriod?._id
      } else {
        newCurrentPeriod = this.analyticAllPeriods.find((period) => DateTime.fromFormat(period?.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM') === value)
        this.analysedPeriodId = newCurrentPeriod?._id
        this.analysedPeriod = DateTime.fromFormat(newCurrentPeriod?.initialDate, 'yyyy-MM-dd').toFormat('yyyy-MM')
        this.setDreAnalysedPeriod(this.analysedPeriod)
      }
      if (newCurrentPeriod) {
        this.period = [
          DateTime.fromFormat(newCurrentPeriod.initialDate, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
          DateTime.fromFormat(newCurrentPeriod.finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        ]
        this.consumersPeriod = [
          DateTime.fromFormat(newCurrentPeriod.initialDate, 'yyyy-MM-dd').minus({ days: 1 }).toFormat('yyyyMMdd'),
          DateTime.fromFormat(newCurrentPeriod.finalDate, 'yyyy-MM-dd').toFormat('yyyyMMdd'),
        ]
        try {
          this.loading = true
          await this.getData()
          await this.setDreAnalysedPeriod(this.analysedPeriod)
        } catch (error) {
          console.log(error)
          this.$snackbar({ message: 'Não foi possível encontrar dados para o período selecionado.', snackbarColor: 'error' })
        } finally {
          this.loading = false
        }
      } else {
        this.$store.commit('RESET_DRE_DATA')
        this.$snackbar({ message: 'Não foi possível encontrar dados para o período selecionado.', snackbarColor: 'error' })
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.header-actions {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}

.period-container {
  max-width: 492px;
  h2 {
    font-size: 18px;
    font-weight: 700;
    color: $cinza-d10;
  }

  p {
    font-size: 14px;
    color: $cinza-0;
    line-height: 20px;
  }
}

.club-offers,
.consumers-distribution {
  font-size: 26px;
  color: $cinza-d10;
  font-weight: 400;
}

.club-card-container,
.distribution-container {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
}

.filters-container {
  max-width: 60%;
}

.padding-10 {
  padding: 10px;
}
</style>
